'use client';

import { useEffect, useRef } from 'react';
import { getLenis } from '@/lib/lenisInstance';
import { useQuality } from '@/components/shared/QualityProvider';
// INTRO_PHASE_VH reads from motionTokens (NOT from ./CameraRig) so this
// component — which renders on mobile via autoplay — doesn't drag the
// three.js + R3F runtime into the mobile bundle just to read a number.
import { INTRO_LOGO_PHASES, INTRO_PHASE_VH } from '@/lib/motionTokens';
import { setIsIntroActive } from '@/lib/journeyState';

const FRAGMENTS_BASE = '/logos/logo-fragments';

// Container aspect mirrors the assembled mark's bounding box in the brand
// reference (≈ 530×335px → ~1.59:1).
const CONTAINER_W = 159;
const CONTAINER_H = 100;

const LAYOUT = {
  darkL:  { top: 4,  left: 14, height: 93, src: 'Vector-1.png',  restZ: -50 },
  darkR:  { top: 4,  left: 49, height: 93, src: 'Vector.png',    restZ: -50 },
  innerV: { top: 4,  left: 36, height: 36, src: 'Group 240.png', restZ: -10 },
  lightV: { top: 4,  left: 29, height: 92, src: 'Vector-3.png',  restZ:  28 },
  dot:    { top: 57, left: 1,  height: 39, src: 'Vector-2.png',  restZ:  50 },
} as const;

type FragmentKey = keyof typeof LAYOUT;
const KEYS: readonly FragmentKey[] = ['darkL', 'darkR', 'innerV', 'lightV', 'dot'];

const EXITS: Record<FragmentKey, {
  tx: number; ty: number; tz: number; rx: number; ry: number; rz: number;
}> = {
  dot:    { tx: -120, ty:  130, tz:  260, rx:  42, ry: -38, rz:  48 },
  darkL:  { tx: -135, ty:  -95, tz:  180, rx:  18, ry: -78, rz: -45 },
  lightV: { tx:    0, ty: -170, tz:  380, rx: -62, ry:   8, rz:  18 },
  innerV: { tx:  105, ty:  175, tz: -120, rx:  72, ry:  62, rz: -65 },
  darkR:  { tx:  135, ty:  -95, tz:  180, rx:  18, ry:  78, rz:  45 },
};

type IdleSpec = {
  tx: number; ty: number; tz: number;
  rx: number; ry: number; rz: number;
  px: number; py: number;
  phx: number; phy: number;
};

const IDLE: Record<FragmentKey, IdleSpec> = {
  darkL:  { tx: 1.0, ty: 1.2, tz: 14, rx: 1.4, ry: 2.6, rz: 0.8, px: 8.3, py: 6.7, phx: 1.5, phy: 0.3 },
  darkR:  { tx: 1.1, ty: 1.4, tz: 13, rx: 1.6, ry: 2.8, rz: 1.0, px: 7.9, py: 7.1, phx: 3.7, phy: 5.5 },
  innerV: { tx: 3.5, ty: 4.0, tz: 24, rx: 5.5, ry: 6.5, rz: 4.5, px: 4.7, py: 5.3, phx: 0.8, phy: 2.7 },
  lightV: { tx: 1.4, ty: 2.2, tz: 24, rx: 2.4, ry: 1.4, rz: 1.6, px: 6.5, py: 5.1, phx: 2.3, phy: 4.1 },
  dot:    { tx: 3.0, ty: 2.5, tz: 20, rx: 3.5, ry: 4.5, rz: 2.5, px: 5.7, py: 4.3, phx: 0.0, phy: 1.2 },
};

const TWO_PI = Math.PI * 2;

// Phase boundaries inside the INTRO_PHASE_VH budget (0→1 relative).
//   APPROACH: stage scales toward the camera before the burst.
//   BURST:    fragments fly apart in 3D.
//   FLOAT:    exploded debris drifts with boosted idle.
//   FADE:     alpha drops to 0.
//   BREATHER: empty tail before the hijack engages.
// Tune the boundary values in `lib/motionTokens.ts` → INTRO_LOGO_PHASES.
const { approachEnd: APPROACH_END, burstEnd: BURST_END, fadeStart: FADE_START, fadeEnd: FADE_END } = INTRO_LOGO_PHASES;

/**
 * Hero brand mark over the intro state. Lives as a fixed overlay across
 * the journey scene — its 0→1 progress is driven by the live scroll
 * position (via Lenis), mapped to the journey's INTRO_PHASE_VH window.
 *
 * During that window the journey's hijack is disabled (CameraRig skips
 * its wheel/touch/key handlers below INTRO_PHASE_VH), so the user rolls
 * smoothly through the explosion. Once past the window, the hijack
 * resumes station snapping for the rest of the corridor.
 *
 * Five fragments first approach the camera (perspective Z + scale),
 * then explode in 3D, drift as debris on independent Lissajous orbits,
 * and fade. Skipped on `low` tier and prefers-reduced-motion.
 */
export function IntroLogoBreak() {
  const { tier } = useQuality();
  const stageRef = useRef<HTMLDivElement>(null);
  const refs = useRef<Record<FragmentKey, HTMLImageElement | null>>({
    dot: null,
    darkL: null,
    lightV: null,
    innerV: null,
    darkR: null,
  });

  const enabled = tier === 'high' || tier === 'mid' || tier === 'low';
  // On low tier the journey scroll-corridor is replaced by the inline
  // showcase, so the explosion plays itself instead of being scrubbed.
  const autoplay = tier === 'low';
  const AUTOPLAY_DURATION_SECONDS = 3.5;

  useEffect(() => {
    if (!enabled) return;
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduced) return;

    // Broadcast that the explosion is mid-flight so siblings (ViewToggle,
    // MobileGridIntro) can hide their UI / hold their fade-in until the
    // fragments have cleared. Flipped back to false the first frame
    // progress crosses FADE_END (fragments fully transparent).
    setIsIntroActive(true);
    let signalledDone = false;

    const lenis = getLenis();
    let rafId = 0;
    const startMs = performance.now();

    function tick() {
      const now = (performance.now() - startMs) / 1000;
      let progress: number;
      if (autoplay) {
        progress = Math.min(1, Math.max(0, now / AUTOPLAY_DURATION_SECONDS));
      } else {
        const scrollY = lenis ? lenis.scroll : window.scrollY;
        const budgetPx = (INTRO_PHASE_VH * window.innerHeight) / 100;
        progress = Math.min(1, Math.max(0, scrollY / budgetPx));
      }

      const approachRaw = Math.min(1, Math.max(0, progress / APPROACH_END));
      const approach = approachRaw * approachRaw * (3 - 2 * approachRaw);

      const tTransformRaw = Math.min(
        1,
        Math.max(0, (progress - APPROACH_END) / (BURST_END - APPROACH_END)),
      );
      const t = tTransformRaw * tTransformRaw * (3 - 2 * tTransformRaw);

      const tOpacityRaw = Math.min(
        1,
        Math.max(0, (progress - FADE_START) / (FADE_END - FADE_START)),
      );
      const tOpacity = tOpacityRaw * tOpacityRaw * (3 - 2 * tOpacityRaw);
      const opacity = 1 - tOpacity;

      // Release the "intro active" signal at the BEGINNING of the fade
      // window (not the end) so siblings — particularly MobileIntroGate's
      // 1100ms reveal — start their entrance while the W is still
      // dissolving. The two animations cross-fade for ~0.7s instead of
      // playing back-to-back, which makes mobile read as one continuous
      // motion (W dissolves → portfolio rises through the same beat)
      // rather than "explosion finishes, then content appears".
      if (!signalledDone && progress >= FADE_START) {
        signalledDone = true;
        setIsIntroActive(false);
      }

      const idleAmpBoost = 1 + t * 2.5;
      const idleFade = opacity * idleAmpBoost;

      const stage = stageRef.current;
      if (stage) {
        const stageScale = 0.6 + 0.4 * approach;
        const stageZ = -260 + 260 * approach;
        stage.style.transform = `translateZ(${stageZ}px) scale(${stageScale})`;
      }

      for (const key of KEYS) {
        const el = refs.current[key];
        if (!el) continue;

        const exit = EXITS[key];
        const idle = IDLE[key];
        const restZ = LAYOUT[key].restZ;

        const sx  = Math.sin(now * TWO_PI / idle.px + idle.phx);
        const sy  = Math.sin(now * TWO_PI / idle.py + idle.phy);
        const sz  = Math.sin(now * TWO_PI / (idle.px * 1.3) + idle.phx + 0.7);
        const srx = Math.sin(now * TWO_PI / (idle.py * 1.1) + idle.phy + 1.4);
        const sry = Math.sin(now * TWO_PI / (idle.px * 1.5) + idle.phx + 2.1);
        const srz = Math.sin(now * TWO_PI / idle.py + idle.phy + 0.5);

        const tx = exit.tx * t + idle.tx * sx * idleFade;
        const ty = exit.ty * t + idle.ty * sy * idleFade;
        const tz = restZ + exit.tz * t + idle.tz * sz * idleFade;
        const rx = exit.rx * t + idle.rx * srx * idleFade;
        const ry = exit.ry * t + idle.ry * sry * idleFade;
        const rz = exit.rz * t + idle.rz * srz * idleFade;

        el.style.transform =
          `translate3d(${tx}%, ${ty}%, ${tz}px)` +
          ` rotateX(${rx}deg)` +
          ` rotateY(${ry}deg)` +
          ` rotateZ(${rz}deg)`;
        el.style.opacity = String(opacity);
      }

      rafId = requestAnimationFrame(tick);
    }

    rafId = requestAnimationFrame(tick);

    return () => {
      cancelAnimationFrame(rafId);
      // Belt-and-braces: if unmount happens mid-flight (route change,
      // HMR, dev fast refresh) release the signal so the next page
      // doesn't think the intro is still playing.
      setIsIntroActive(false);
    };
  }, [enabled]);

  if (!enabled) return null;

  return (
    <div
      aria-hidden
      className="pointer-events-none fixed inset-0 z-20 flex items-center justify-center overflow-hidden"
      style={{ perspective: '1400px', perspectiveOrigin: '50% 45%' }}
    >
      <div
        ref={stageRef}
        className="relative h-[24vh] max-h-[200px] md:h-[34vh] md:max-h-[300px]"
        style={{
          aspectRatio: `${CONTAINER_W} / ${CONTAINER_H}`,
          transformStyle: 'preserve-3d',
          willChange: 'transform',
        }}
      >
        {KEYS.map((key) => {
          const cfg = LAYOUT[key];
          return (
            <img
              key={key}
              ref={(el) => {
                refs.current[key] = el;
              }}
              src={`${FRAGMENTS_BASE}/${cfg.src}`}
              alt=""
              aria-hidden
              draggable={false}
              className="absolute w-auto select-none"
              style={{
                top: `${cfg.top}%`,
                left: `${cfg.left}%`,
                height: `${cfg.height}%`,
                backfaceVisibility: 'hidden',
                willChange: 'transform, opacity',
              }}
            />
          );
        })}
      </div>
    </div>
  );
}
